import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import HistoryPage from './pages/HistoryPage';
import SimilaritySearchPage from './pages/SimilaritySearchPage';
import AnalysisViewPage from './pages/AnalysisViewPage';
import ExcelConfigPage from './pages/ExcelConfigPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import AccountsPage from './pages/AccountsPage';
import UsersPage from './pages/UsersPage';
import PromptsPage from './pages/PromptsPage';
import FolderBrowserPage from './pages/FolderBrowserPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderScanPage from './pages/OrderScanPage';
import OfferPage from './pages/OfferPage';
import PartHistoryPage from './pages/PartHistoryPage';
import OfferEditorPage from './pages/OfferEditorPage';
import OfferPdfPage from './pages/OfferPdfPage';
import DashboardPage from './pages/DashboardPage';
import OffersPage from './pages/OffersPage';
import ConsumptionDashboardPage from './pages/ConsumptionDashboardPage';
import AccountConsumptionPage from './pages/AccountConsumptionPage';
import TariffsPage from './pages/TariffsPage';
import Layout from './components/Layout/Layout';
import { isAuthenticated, fetchAccountFlags } from './services/auth';
import { getAccountSettings } from './services/settings';
import { applyTheme } from './utils/themeUtils';

const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

// Pedidos y ofertas son de las cuentas que tienen el módulo. El menú ya no las enseña,
// pero sin esto se podía entrar escribiendo la dirección y aparecía una pantalla que
// no guarda nada, porque el servidor la rechaza.
const OrdersRoute = ({ children }) => {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    fetchAccountFlags()
      .then((account) => setEnabled(Boolean(account.orders_enabled)))
      .catch(() => setEnabled(false));
  }, []);

  if (enabled === null) return null;
  // A la pantalla de inicio, no a "/": esa ruta manda al login y parecería que se ha
  // caducado la sesión cuando lo único que pasa es que la cuenta no tiene el módulo.
  if (!enabled) return <Navigate to="/similarity-search" replace />;
  return children;
};

function App() {
  useEffect(() => {
    const loadTheme = async () => {
      if (isAuthenticated()) {
        try {
          const settings = await getAccountSettings();
          if (settings.theme) {
            applyTheme(settings.theme);
          }
        } catch (error) {
          console.error('Error loading theme:', error);
        }
      }
    };
    
    loadTheme();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <HistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/panel"
          element={
            <ProtectedRoute>
              <OrdersRoute><DashboardPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <OrdersRoute><OrdersPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/new"
          element={
            <ProtectedRoute>
              <OrdersRoute><OrderScanPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers"
          element={
            <ProtectedRoute>
              <OrdersRoute><OffersPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/new"
          element={
            <ProtectedRoute>
              <OrdersRoute><OfferEditorPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/calculadora"
          element={
            <ProtectedRoute>
              <OrdersRoute><OfferPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/history"
          element={
            <ProtectedRoute>
              <OrdersRoute><PartHistoryPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/:offerHash"
          element={
            <ProtectedRoute>
              <OrdersRoute><OfferEditorPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/:offerHash/pdf"
          element={
            <ProtectedRoute>
              <OrdersRoute><OfferPdfPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:orderHash"
          element={
            <ProtectedRoute>
              <OrdersRoute><OrderDetailPage /></OrdersRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/similarity-search"
          element={
            <ProtectedRoute>
              <SimilaritySearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis/:hash"
          element={
            <ProtectedRoute>
              <AnalysisViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/excel-config"
          element={
            <ProtectedRoute>
              <ExcelConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-settings"
          element={
            <ProtectedRoute>
              <AccountSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/prompts"
          element={
            <ProtectedRoute>
              <PromptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/folder-browser"
          element={
            <ProtectedRoute>
              <FolderBrowserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consumption"
          element={
            <ProtectedRoute>
              <ConsumptionDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consumption/:accountId"
          element={
            <ProtectedRoute>
              <AccountConsumptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tariffs"
          element={
            <ProtectedRoute>
              <TariffsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/login" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;